import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { Fragment, useEffect, useMemo } from 'react'

import { BackContinue } from '@components/back_continue/back_continue'
import { useBookingContext } from '@providers/booking'
import { MAX_WIDGET_SIZE, STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { i18WithParams as t } from '@shared/locale'
import { areGroomsAllSet } from '@shared/offers'
import { useRoutes } from 'app/use_routes'
import { hasShowPreferredProvider } from 'components-ui/src/shared/location'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { useRedirectNoData } from 'hooks/use_redirect_no_data'
import { GroomerSelector } from './groomer_selector'
import { getPetOffers } from './helper'
import { getInStayLBC } from './request_dates/helper'
import { RequestDates } from './request_dates/request_dates'
import { useBookingConf } from './use_booking_conf'
import { useParams } from 'react-router-dom'
import { getReservationPetIds } from '@components/double_booking/helper'
import { BookingConflictModal } from '@components/double_booking/booking_conflict_modal'
import { buildGroomingVisitPeriod } from '@components/double_booking/conflict_check'
import { useBookingConflictContinueGuard } from '@components/double_booking/use_booking_conflict_continue_guard'
import { useBookingContinue, useBookingContinuePolicy } from '@providers/booking_continue'

const GroomingWizardInner = () => {
  const bookingCtx = useBookingContext()
  const { continueBooking, isContinuing } = useBookingContinue()
  const { navigateRoute } = useRoutes()
  const { accountName: locationName = '' } = useParams()
  const conflict = useBookingConflictContinueGuard(locationName, {
    period: buildGroomingVisitPeriod(bookingCtx.state),
    locationPetProfileIds: getReservationPetIds(bookingCtx.state),
  })

  const standaloneGroom = bookingCtx.state.serviceTypeName === 'grooming'
  const groups = getPetOffers(bookingCtx)

  const onContinueHandler = async (): Promise<boolean> => {
    await continueBooking()
    return false
  }

  useBookingContinuePolicy({
    owner: 'grooming',
    guards: standaloneGroom ? [conflict.guard] : [],
  })

  const checkProvider = hasShowPreferredProvider(getInStayLBC(bookingCtx.state, 'grooming'))
  if (!checkProvider) {
    groups.forEach((group) => (group.state = 'dateSelector'))
  }

  const noGrooms = useMemo(() => {
    if (groups.length) {
      return <></>
    }
    if (standaloneGroom) {
      const goBack = () => {
        bookingCtx.dispatch({ type: 'RESET_STATE_FULL' })
        setTimeout(() => navigateRoute('searchServiceType', { serviceTypeName: 'grooming' }), 0)
      }
      return (
        <>
          <Typography variant="h6" color="text.disabled" sx={{ py: 4 }}>
            {t('groomingWizard.noGrooming.inStay')}
          </Typography>
          <Button onClick={goBack}>{t('groomingWizard.noGrooming.inStay.action')}</Button>
        </>
      )
    }
    return (
      <Typography variant="h6" color="text.disabled" sx={{ py: 4 }}>
        {t('groomingWizard.noGrooming')}
      </Typography>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCtx.state.serviceTypeName, groups.length])
  const canContinue = useMemo(() => areGroomsAllSet(bookingCtx), [bookingCtx])

  return (
    <Box sx={{ maxWidth: MAX_WIDGET_SIZE, margin: '0 auto' }}>
      <Spacer height={3} />
      <Typography variant="h1" sx={{ px: 2 }}>
        {t('groomingWizard.title')}
      </Typography>
      <Spacer height={3} />

      {noGrooms}

      {groups.map((group) => {
        const key = group.pet.id
        switch (group.state) {
          case 'groomer':
            return (
              <Fragment key={key}>
                <GroomerSelector groomOffer={group} />
                <Spacer height={3} />
              </Fragment>
            )
          case 'dateSelector':
            return <RequestDates key={key} groomOffer={group} checkProvider={checkProvider} />
          default:
            return null
        }
      })}
      <BookingConflictModal {...conflict.modalProps} />
      <Spacer height={STICKY_FOOTER_HEIGHT} />
      <BackContinue onContinue={() => onContinueHandler()} continueEnabled={canContinue && !isContinuing} />
    </Box>
  )
}

export const GroomingWizard = () => {
  const bookingCtx = useBookingContext()
  const lbc = useBookingConf()

  useRedirectNoData()

  useEffect(() => {
    if (bookingCtx.state.currentState !== 'grooming') {
      bookingCtx.dispatch({ type: 'SET_SEARCH_STATE', payload: { value: 'grooming' } })
    }
  })

  if (lbc.isLoading || lbc.isError) {
    return <Skeleton variant="rounded" height={48} sx={{ mb: 2 }} />
  } else {
    return <GroomingWizardInner />
  }
}
